<template>
  <tr class="draggable-row" :class="{ 'is-dragging': isDragging }">
      <td v-if="showDragHandle" class="text-center align-middle drag-handle">
        <font-awesome-icon :icon="faGripLines" />
      </td>

      <td class="text-center align-items-center">{{ index + 1 }}</td>

      <td class="align-top">
        <div v-if="isVttMode" class="media-assignment">
          <textarea
              :id="getMediaFieldId()"
              v-model="textContentModel"
              class="form-control"
              rows="3"
              placeholder="Enter closed caption text..."
              maxlength="500"
          ></textarea>
        </div>

        <div v-else class="media-assignment">
          <div class="btn-group btn-group-sm mb-2" role="group">
            <button
                type="button"
                :class="['btn', event.mediaType === MediaType.IMAGE ? 'btn-primary' : 'btn-outline-secondary']"
                @click="emit('set-media-type', MediaType.IMAGE)"
                :disabled="isMediaTypeLocked"
                :title="isMediaTypeLocked ? 'Cannot change media type after media is selected' : ''"
            >
              Image
            </button>
            <button
                type="button"
                :class="['btn', event.mediaType === MediaType.MP3 ? 'btn-primary' : 'btn-outline-secondary']"
                @click="emit('set-media-type', MediaType.MP3)"
                :disabled="isMediaTypeLocked"
                :title="isMediaTypeLocked ? 'Cannot change media type after media is selected' : ''"
            >
              MP3
            </button>
            <button
                type="button"
                :class="['btn', event.mediaType === MediaType.MP4 ? 'btn-primary' : 'btn-outline-secondary']"
                @click="emit('set-media-type', MediaType.MP4)"
                :disabled="isMediaTypeLocked"
                :title="isMediaTypeLocked ? 'Cannot change media type after media is selected' : ''"
            >
              MP4
            </button>
            <button
                type="button"
                :class="['btn', event.mediaType === MediaType.TEXT ? 'btn-primary' : 'btn-outline-secondary']"
                @click="emit('set-media-type', MediaType.TEXT)"
                :disabled="isMediaTypeLocked"
                :title="isMediaTypeLocked ? 'Cannot change media type after media is selected' : ''"
            >
              Text
            </button>
            <button
                type="button"
                :class="['btn', event.mediaType === MediaType.HTML ? 'btn-primary' : 'btn-outline-secondary']"
                @click="emit('set-media-type', MediaType.HTML)"
                :disabled="isMediaTypeLocked"
                :title="isMediaTypeLocked ? 'Cannot change media type after media is selected' : ''"
            >
              HTML
            </button>
          </div>
          <file-picker-input
              v-if="event.mediaType !== MediaType.TEXT && event.mediaType !== MediaType.HTML"
              v-model="mediaUrlModel"
              :media-type="getLfmType(event.mediaType)"
              :input-id="getMediaFieldId()"
              placeholder="Select media file..."
          />
          <div v-else-if="event.mediaType === MediaType.TEXT" class="input-group">
            <input
                :id="getMediaFieldId()"
                v-model="textContentModel"
                type="text"
                class="form-control"
                placeholder="Enter text content..."
            >
          </div>
          <div v-else class="input-group">
            <textarea
                :id="getMediaFieldId()"
                v-model="htmlContentModel"
                class="form-control"
                rows="4"
                placeholder="Enter HTML content..."
            ></textarea>
          </div>

          <closed-captions-section
              v-if="(event.mediaType === MediaType.MP3 || event.mediaType === MediaType.MP4) && event.mediaUrl"
              mode="per-event"
              :events="event.perMediaCaptions || []"
              :per-event-options="{
                expanded: props.showCaptions,
                uploadDisabled: hasCaptions,
                tableBelowRow: true
              }"
              class="mt-2"
              @upload-vtt="emit('upload-vtt')"
              @toggle-captions="toggleCaptions"
          />
        </div>
      </td>

      <td class="align-top">
        <div class="time-range-wrapper pt-0">
          <div class="d-flex align-items-center mb-2">
            <div class="flex-grow-1">
              <input
                  v-model="startTimeModel"
                  type="text"
                  class="form-control form-control-sm time-input"
                  :class="{ 'is-invalid': !isValidTime(event.startTime) && event.startTime }"
                  placeholder="0:00"
                  required
                  @blur="emit('format-time', 'startTime')"
              >
            </div>

            <div class="mx-2 text-muted">
              <i class="fa fa-arrow-right"></i>
            </div>

            <div class="flex-grow-1">
              <input
                  v-model="endTimeModel"
                  :disabled="!isVttMode && event.untilEnd"
                  type="text"
                  class="form-control form-control-sm time-input"
                  :class="{
                    'is-invalid': (!event.untilEnd && !isValidTime(event.endTime) && event.endTime) ||
                                 (!event.untilEnd && isEndTimeBeforeStart)
                  }"
                  :placeholder="!isVttMode && event.untilEnd ? 'Until end' : '0:00'"
                  required
                  @blur="emit('format-time', 'endTime')"
              >
            </div>
          </div>

          <div v-if="!isVttMode" class="d-flex justify-content-between align-items-center">
            <div class="custom-control custom-checkbox d-flex align-items-center">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="'until-end-' + index"
                  v-model="untilEndModel"
              >
              <label class="custom-control-label small text-muted mb-0" :for="'until-end-' + index"
                     style="cursor: pointer;">
                Until End
              </label>
            </div>

            <div class="custom-control custom-checkbox d-flex align-items-center">
              <span v-if="!event.untilEnd && eventDuration > 0">
                <span class="small text-muted font-weight-bold mr-1 mb-0">
                  <i class="fa fa-clock-o mr-1"></i> Duration:
                </span>
                <span class="badge badge-light border text-muted font-weight-normal mb-0">
                  {{ formatDuration(eventDuration) }}
                </span>
              </span>
            </div>
          </div>

          <small v-if="!isValidTime(event.startTime) && event.startTime" class="text-danger mt-1 d-block">
            Invalid start time format
          </small>
          <small v-if="!event.untilEnd && !isValidTime(event.endTime) && event.endTime"
                 class="text-danger mt-1 d-block">
            Invalid end time format
          </small>
          <small v-if="!event.untilEnd && isEndTimeBeforeStart" class="text-danger mt-1 d-block">
            End time must be after start time
          </small>
        </div>
      </td>

      <td class="align-top">
        <div class="group-easing-wrapper">
          <div v-if="event.mediaType === MediaType.MP3 && !isVttMode">
            <div class="small text-muted">
              <i class="fa fa-info-circle mr-1"></i>
              Group ID and easing effects are not applicable to MP3 media.
            </div>
          </div>

          <div v-else>
            <div class="mb-2">
              <input
                  v-model="groupIdModel"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Enter Group ID"
              >
            </div>

            <div class="row no-gutters">
              <div class="col-6 pr-1">
                <label class="small text-muted mb-1 d-block">Easing In</label>
                <select
                    v-model="easingInModel"
                    class="form-control form-control-sm"
                >
                  <option v-for="option in EASING_IN_OPTIONS" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
              <div class="col-6 pl-1">
                <label class="small text-muted mb-1 d-block">Easing Out</label>
                <select
                    v-model="easingOutModel"
                    class="form-control form-control-sm"
                >
                  <option v-for="option in EASING_OUT_OPTIONS" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </td>

      <td class="text-center align-middle">
        <button
            type="button"
            class="btn btn-sm btn-danger"
            @click="emit('remove')"
            title="Delete Event"
        >
          <i class="fa fa-trash"></i>
        </button>
      </td>
    </tr>

    <tr v-if="hasCaptions && props.showCaptions && !isVttMode" class="captions-row">
      <td colspan="6" class="captions-content p-0">
        <div class="captions-section">
          <timeline-events-table
              :events="event.perMediaCaptions || []"
              :show-drag-handle="true"
              :is-vtt-mode="true"
              :empty-message="'No captions added yet.'"
              @update:events="updateEventCaptions"
              @set-media-type="() => {}"
              @format-time="(captionIndex, field) => emit('format-caption-time', captionIndex, field)"
              @until-end-change="() => {}"
              @update-field="(captionIndex, field, value) => emit('update-caption', captionIndex, field, value)"
              @remove="(captionIndex) => emit('remove-caption', captionIndex)"
          />
          <div class="mt-3 px-3 pb-3">
            <div class="d-flex justify-content-between align-items-center">
              <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="!canAddCaption"
                  :title="!canAddCaption ? 'Please complete the current cue (Text, Start Time, End Time) before adding a new one' : ''"
                  @click="emit('add-caption')"
              >
                <i class="fa fa-plus"></i> Add Cue
              </button>
              <button type="button" class="btn btn-secondary btn-sm" @click="emit('clear-vtt')" title="Clear captions">
                <i class="fa fa-times"></i> Clear
              </button>
            </div>
            <small v-if="!canAddCaption" class="text-danger d-block mt-2">
              Complete the current cue (Text, Start Time, End Time) before adding a new one
            </small>
          </div>
        </div>
      </td>
    </tr>
</template>

<script setup>
import {computed, inject} from 'vue'
import FilePickerInput from './FilePickerInput.vue'
import TimelineEventsTable from './TimelineEventsTable.vue'
import ClosedCaptionsSection from './TimelineClosedCaptionsSection.vue'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {faGripLines} from '@fortawesome/free-solid-svg-icons'
import {
  EASING_IN_OPTIONS,
  EASING_OUT_OPTIONS,
  MediaType
} from '../../common/animation-constants'
import {isValidTime, formatDuration} from '../../common/time-utils'

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isDragging: {
    type: Boolean,
    default: false
  },
  showDragHandle: {
    type: Boolean,
    default: true
  },
  isVttMode: {
    type: Boolean,
    default: false
  },
  eventDuration: {
    type: Number,
    default: 0
  },
  isEndTimeBeforeStart: {
    type: Boolean,
    default: false
  },
  showCaptions: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['set-media-type', 'format-time', 'until-end-change', 'update-field', 'remove', 'upload-vtt', 'clear-vtt', 'format-caption-time', 'update-caption', 'remove-caption', 'add-caption', 'toggle-captions', 'update-captions'])

const captionValidation = inject('captionValidation', {
  canAddCaption: () => true
})

const canAddCaption = computed(() => captionValidation.canAddCaption(props.index))

const hasCaptions = computed(() => props.event.perMediaCaptions && props.event.perMediaCaptions.length > 0)

const isMediaTypeLocked = computed(() => !!(props.event.mediaUrl || props.event.textContent || props.event.htmlContent))

// Computed properties for v-model binding
const startTimeModel = computed({
  get: () => props.event.startTime,
  set: (value) => emit('update-field', 'startTime', value)
})

const endTimeModel = computed({
  get: () => props.event.endTime,
  set: (value) => emit('update-field', 'endTime', value)
})

const textContentModel = computed({
  get: () => props.event.textContent,
  set: (value) => emit('update-field', 'textContent', value)
})

const htmlContentModel = computed({
  get: () => props.event.htmlContent,
  set: (value) => emit('update-field', 'htmlContent', value)
})

const groupIdModel = computed({
  get: () => props.event.groupId,
  set: (value) => emit('update-field', 'groupId', value)
})

const easingInModel = computed({
  get: () => props.event.easingIn,
  set: (value) => emit('update-field', 'easingIn', value)
})

const easingOutModel = computed({
  get: () => props.event.easingOut,
  set: (value) => emit('update-field', 'easingOut', value)
})

const untilEndModel = computed({
  get: () => props.event.untilEnd,
  set: (value) => {
    emit('update-field', 'untilEnd', value)
    emit('until-end-change')
  }
})

const mediaUrlModel = computed({
  get: () => props.event.mediaUrl,
  set: (value) => emit('update-field', 'mediaUrl', value)
})

// Helper functions
const getMediaFieldId = () => `media-${props.index}`

const getLfmType = (mediaType) => {
  if (mediaType === MediaType.IMAGE) {
    return 'image'
  } else if (mediaType === MediaType.MP3) {
    return 'audio'
  } else if (mediaType === MediaType.MP4) {
    return 'video'
  }
  return 'file'
}

const toggleCaptions = () => {
  emit('toggle-captions')
}

const updateEventCaptions = (newCaptions) => {
  emit('update-captions', newCaptions)
}
</script>

<style lang="scss" scoped>
.draggable-row {
  &.is-dragging {
    cursor: grabbing;
  }

  td.drag-handle {
    border-right: none;
    cursor: grab;
  }

  td.text-center.align-items-center {
    border-left: none;
  }

  .btn {
    transition: all 0.15s ease-in-out;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  .btn-group.dropdown {
    position: static;
  }
}

.media-assignment {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  textarea {
    resize: vertical;
    min-height: 70px;
    font-size: 0.9rem;
    line-height: 1.5;
  }
}

.time-range-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;

  .time-input {
    font-family: 'Courier New', monospace;
    font-weight: 500;
    text-align: center;

    &.is-invalid {
      border-color: #dc3545;
      padding-right: 0 !important;
    }

    &:disabled {
      background-color: #e9ecef;
    }
  }

  .fa-arrow-right {
    font-size: 0.875rem;
    color: #6c757d;
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    white-space: nowrap;
  }

  .custom-control {
    margin-top: 0.5rem;
    margin-bottom: 0;

    .custom-control-label {
      font-size: 0.8rem;
      line-height: 1.4;

      i {
        font-size: 0.75rem;
      }
    }
  }

  small.text-danger {
    font-size: 0.7rem;
    line-height: 1.2;
    margin-top: 0.25rem;
  }
}

.group-easing-wrapper {
  .row {
    margin-left: 0;
    margin-right: 0;
  }

  label {
    font-size: 0.75rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
}

.captions-row {
  background-color: #fafbfc;

  .captions-spacer {
    background-color: #fafbfc;
    border-top: none !important;
    padding: 0 !important;
  }

  .captions-content {
    background-color: #fff;
    border-top: 1px solid #dee2e6;
    vertical-align: top;
  }

  .captions-section {
    padding: 0.75rem 1rem;
  }
}
</style>
